import type { CSSProperties, ImgHTMLAttributes } from "react";

/**
 * Responsive <picture> that emits AVIF → WebP → JPG sources with consistent
 * `sizes`, `fetchpriority`, and `decoding` attributes across the site.
 *
 * Usage:
 *   <Picture
 *     sources={{
 *       avif: [{ src: hero960Avif, w: 960 }, { src: hero1280Avif, w: 1280 }, { src: hero1920Avif, w: 1920 }],
 *       webp: [{ src: hero960Webp, w: 960 }, { src: hero1280Webp, w: 1280 }, { src: hero1920Webp, w: 1920 }],
 *       jpg:  [{ src: hero960Jpg,  w: 960 }, { src: hero1280Jpg,  w: 1280 }, { src: hero1920Jpg,  w: 1920 }],
 *     }}
 *     fallback={hero1920Jpg}
 *     width={1920}
 *     height={1080}
 *     alt=""
 *     priority           // LCP image — sets fetchpriority="high" + decoding="sync"
 *     sizes="(max-width: 640px) 960px, (max-width: 1280px) 1280px, 1920px"
 *     className="absolute inset-0 h-full w-full object-cover"
 *   />
 *
 * Non-LCP usage is identical but omit `priority` — the component defaults to
 * `loading="lazy"` + `decoding="async"` + `fetchpriority="auto"` so below-fold
 * images don't fight the LCP for network priority.
 *
 * Source order MATTERS: the browser picks the FIRST <source> whose `type` it
 * supports, so AVIF must come before WebP, and WebP before JPEG. The <img>
 * fallback is what legacy browsers (and search bots) actually render.
 */
export interface PictureVariant {
  /** Imported asset URL (bundler returns a hashed string at build time). */
  src: string;
  /** Intrinsic pixel width of this variant — used to build the `srcset` "Nw" descriptor. */
  w: number;
}

export interface PictureSources {
  /** AVIF variants — typically ~45% smaller than WebP. Optional. */
  avif?: PictureVariant[];
  /** WebP variants — ~25–30% smaller than JPEG. Optional. */
  webp?: PictureVariant[];
  /** JPEG variants — required fallback for maximum compatibility. */
  jpg: PictureVariant[];
}

export interface PictureProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet" | "sizes" | "loading" | "decoding" | "fetchPriority"> {
  /** Multi-format source map. AVIF/WebP optional; JPEG required as last-ditch fallback. */
  sources: PictureSources;
  /** The `src` used by the `<img>` inside `<picture>`. Typically the largest JPEG variant. */
  fallback: string;
  /** Required alt text. Use "" + aria-hidden for purely decorative images. */
  alt: string;
  /** Intrinsic width in pixels — required to prevent CLS. */
  width: number;
  /** Intrinsic height in pixels — required to prevent CLS. */
  height: number;
  /**
   * CSS `sizes` descriptor. Applied to every `<source>` so all formats use the
   * same breakpoint logic. Example: `(max-width: 640px) 960px, 1920px`.
   * Default: `100vw`.
   */
  sizes?: string;
  /**
   * Mark as LCP / above-the-fold. Sets `fetchpriority="high"`, `decoding="sync"`,
   * and skips `loading="lazy"`. Use for at most ONE image per page.
   */
  priority?: boolean;
  /** Override the wrapper `<picture>` style (rarely needed). */
  pictureStyle?: CSSProperties;
  /** Override the wrapper `<picture>` className (rarely needed). */
  pictureClassName?: string;
}

function toSrcSet(variants: PictureVariant[]): string {
  return variants.map((v) => `${v.src} ${v.w}w`).join(", ");
}

export function Picture({
  sources,
  fallback,
  alt,
  width,
  height,
  sizes = "100vw",
  priority = false,
  pictureStyle,
  pictureClassName,
  ...imgProps
}: PictureProps) {
  // Priority images (LCP) skip native lazy-loading and signal high fetch
  // priority so the preload scanner picks them up ASAP. We keep decoding
  // async for LCP too — `decoding="sync"` would block paint on the decode,
  // which hurts LCP. Non-priority images default to lazy + async decode so
  // they never compete with the LCP for network/CPU budget.
  const loading = priority ? undefined : "lazy";
  const decoding = "async";
  const fetchPriority = priority ? "high" : "auto";

  return (
    <picture className={pictureClassName} style={pictureStyle}>
      {sources.avif && sources.avif.length > 0 ? (
        <source type="image/avif" srcSet={toSrcSet(sources.avif)} sizes={sizes} />
      ) : null}
      {sources.webp && sources.webp.length > 0 ? (
        <source type="image/webp" srcSet={toSrcSet(sources.webp)} sizes={sizes} />
      ) : null}
      <source type="image/jpeg" srcSet={toSrcSet(sources.jpg)} sizes={sizes} />
      <img
        {...imgProps}
        src={fallback}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        decoding={decoding}
        // React 19 serializes the camelCase `fetchPriority` prop to the
        // lowercase `fetchpriority` HTML attribute in the real DOM at
        // runtime. SSR emits camelCase literally, but HTML attributes are
        // case-insensitive per the spec so every browser still honours it.
        fetchPriority={fetchPriority}
      />
    </picture>
  );
}
